<template>
  <e-chart
    v-if="hasData"
    :options="chartOptions"
    autoresize
    class="apache-echarts-default-graph"
  ></e-chart>
  <div v-else-if="loaded">
    {{ $t("No Data Available") }}
  </div>
  <SpinnerCmpt v-else></SpinnerCmpt>
</template>

<script>
import moment from 'moment';

import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import granularity from '@/xvisor/utilities/granularity';
import themeStyle from '@/xvisor/utilities/themeStyle';
import timeFormat from '@/xvisor/utilities/timeFormat';

const colors = {
  'TCP RTT': colorPaletteShade.blue4,
  'TCP RTT Expected Value': colorPaletteShade.green4,
  'UDP RTT': colorPaletteShade.pink4,
  'UDP RTT Expected Value': colorPaletteShade.green4,
  'UDP client to server loss': colorPaletteShade.purple5,
  'UDP client to server loss Expected Value': colorPaletteShade.purple4,
  'UDP server to client loss': colorPaletteShade.green5,
  'UDP server to client loss Expected Value': colorPaletteShade.green4,
  'UDP client to server jitter': colorPaletteShade.teal5,
  'UDP client to server jitter Expected Value': colorPaletteShade.teal4,
  'UDP server to client jitter': colorPaletteShade.indigo5,
  'UDP server to client jitter Expected Value': colorPaletteShade.indigo4,
  'TCP Client Retrans': colorPaletteShade.blue7,
  'TCP Client Retrans Expected Value': colorPaletteShade.blue4,
  'TCP Server Retrans': colorPaletteShade.red7,
  'TCP Server Retrans Expected Value': colorPaletteShade.red4,
};

export default {
  components: {
    SpinnerCmpt,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    eventUrl: {
      type: String,
      required: true,
    },
    metric: {
      type: String,
      required: true,
      validator: (value) => ['rtt', 'retrans', 'udpLoss', 'udpRtt', 'udpJitter'].includes(value),
    },
    issueTimeSelected: {
      type: Number,
    },
  },
  data() {
    return {
      series: [],
      eventSeries: [],
      loaded: false,
    };
  },
  watch: {
    url() {
      this.httpGet();
    },
    timeRange() {
      this.httpGet();
    },
  },
  mounted() {
    this.httpGet();
  },
  computed: {
    timeSet() {
      return new Set(this.series.map((ts) => ts.points.map((point) => point.time)).flat());
    },
    hasData() {
      return this.loaded && this.series && this.series.length > 0;
    },
    toggleColor() {
      return themeStyle.styleToggle(colorPalette.black, colorPalette.white);
    },
    toggleTooltipColor() {
      return themeStyle.styleToggle(colorPalette.white, colorPalette.tooltipBackground);
    },
    initialMarkline() {
      // Initializes the line to the end of the graph.
      const times = [...this.timeSet].map((time) => moment(time).valueOf());
      const seriesData = this.series
        .filter((ts) => ts.points.length !== 0)
        .map((ts) => ({
          data: times.map((time) => {
            const timePoint = ts.points.find((point) => moment(point.time).valueOf() === time);
            return [time, timePoint ? timePoint.value : 0];
          }),
        }));
      return seriesData[0].data.at(-1)[0];
    },
    timeSeriesFormat() {
      return timeFormat.momentFormat(this.timeSet, new Date());
    },
    formattedSeries() {
      const times = [...this.timeSet].map((time) => moment(time).valueOf());
      return this.series
        .filter((ts) => ts.points.length !== 0)
        .map((ts) => ({
          name: ts.name,
          type: 'line',
          showSymbol: false,
          itemStyle: {
            color: colors[ts.name],
          },
          lineStyle: {
            type: ts.name.includes('Expected') ? 'dashed' : 'solid',
          },
          markLine: {
            symbol: 'circle',
            lineStyle: {
              type: 'solid',
            },
            data: [{ xAxis: this.issueTimeSelected ? this.issueTimeSelected : this.initialMarkline }],
            label: {
              formatter: (info) => moment(info.value).format(this.timeSeriesFormat),
            },
          },
          markPoint: {
            symbol: 'triangle',
            symbolSize: 15,
            label: {
              offset: [0, 5],
              fontSize: 10,
            },
            itemStyle: {
              color: 'orange',
            },
            tooltip: {
              trigger: 'item',
              formatter: (info) => {
                const cell = info.data;
                const header = `
                  <div class="apache-echarts-tooltip">
                    <div>${info.marker}${moment(cell.xAxis).format(this.timeSeriesFormat)}</div>
                `;
                const eventsRow = `<div># Events: ${cell.value > 0 ? cell.value.toFixed(0) : 0}</div>`;
                let eventMsgs = '';
                eventMsgs = `
                    <div>${cell.name}: ${cell.msg}.</div>
                  `;
                return `${[header, eventsRow, ...eventMsgs].join('')}</div>`;
              },
            },
            data: this.eventSeries ? this.markPointHelper : [],
          },
          data: times.map((time) => {
            const timePoint = ts.points.find((point) => moment(point.time).valueOf() === time);
            return [time, timePoint ? timePoint.value : 0];
          }),
        }));
    },
    chartTitle() {
      if (this.metric === 'rtt') return 'TCP RTT';
      if (this.metric === 'retrans') return 'TCP Retransmissions';
      if (this.metric === 'udpLoss') return 'UDP Loss';
      if (this.metric === 'udpRtt') return 'UDP RTT';
      if (this.metric === 'udpJitter') return 'UDP Jitter';
      return '';
    },
    chartUnit() {
      if (this.metric === 'rtt') return 'ms';
      if (this.metric === 'retrans') return '%';
      if (this.metric === 'udpLoss') return '%';
      if (this.metric === 'udpRtt') return 'ms';
      if (this.metric === 'udpJitter') return 'ms';
      return '';
    },
    chartOptions() {
      return {
        tooltip: {
          trigger: 'axis',
          formatter: (info) => `
            <div class="apache-echarts-tooltip">
              ${this.chartTitle} / ${moment(info[0].value[0]).format(this.timeSeriesFormat)}
              <br>
              ${this.chartTooltipContent(info)}
            </div>
          `,
        },
        grid: [
          {
            left: 50,
            right: 50,
            top: 30,
            bottom: 30,
          },
        ],
        xAxis: {
          type: 'time',
          boundaryGap: false,
          splitLine: {
            show: false,
          },
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            formatter: (value) => moment(value).format(this.timeSeriesFormat),
            fontSize: 9,
          },
        },
        yAxis: [
          {
            type: 'value',
            name: this.chartUnit,
            nameLocation: 'center',
            nameGap: 35,
            axisTick: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLine: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
            axisLabel: {
              formatter: (value) => value.toFixed(1),
              fontSize: 9,
            },
          },
        ],
        series: this.formattedSeries,
      };
    },
    markPointHelper() {
      return this.eventSeries.map((event) => ({
        xAxis: event.time,
        yAxis: event.value,
        // There will always be 1 event for each marker.
        value: 1,
        baseline: event.expectedValue,
        msg: event.msg,
        type: event.type,
        name: event.name,
      }));
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;
      const seriesRequest = this.$http.get(this.url, {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          stepMinutes: granularity.granularity72(this.timeRange),
        },
      });
      const eventSeriesRequest = this.$http.get(this.eventUrl, {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
        },
      });
      eventSeriesRequest
        .then((eventRes) => {
          this.eventSeries = eventRes.data;
        })
        .catch(() => {
          this.eventSeries = [];
        });
      seriesRequest
        .then((tsRes) => {
          this.series = tsRes.data;
        })
        .finally(() => {
          this.loaded = true;
        });
    },
    chartTooltipContent(info) {
      let result = '';
      for (let i = 0; i < info.length; i += 1) {
        const capitalizedSeriesName = info[i].seriesName
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        result += `${info[i].marker} ${capitalizedSeriesName}: ${info[i].value[1].toFixed(1)} ${this.chartUnit} <br>`;
      }
      return result;
    },
  },
};
</script>
